import { NumberFormatBase } from "react-number-format";
import { useHistory } from "react-router-dom";
import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx/xlsx.mjs";
import * as _ from "lodash";

// components
import AnimationLoading from "../../../components/Animationloading";
import { errorSwal, successSwal } from "../../../helper/sweetalert";
import { COLOR_WHITE, LINEAR_GRADIENT_BG } from "../../../const";

// apollo-client
import { CREATE_MANAGEMENT_PRODUCTS, CREATE_MANY_PRODUCTS } from "../../../apollo/product/Mutation";
import { useLazyQuery, useMutation } from "@apollo/client";

// icons
import { faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  Button,
  ButtonGroup,
  Card,
  Form,
  Spinner,
  Table,
  Row,
  Col,
  Badge
} from "react-bootstrap";
import BreadCrumbComponent from "../../../components/BreadCrumbComponent";
import { GET_SUVIVALS } from "../../../apollo/unit/Query";
import ExcelFormProductManage from "./ExcelFormProductManage";
import { HandleCurrency } from "../../../helper";

function AddProductManagement() {
  const history = useHistory();
  const [stockDatas, setStockDatas] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [survivalDatas, setSurvivalDatas] = useState([]);
  const [selectSurvival, setSelectSurvival] = useState();
  const [importStep, setImportStep] = useState(1); // 1: Select fields, 2: Import data, 3: Review imported data
  const [customFormExcel, setCustomFormExcel] = useState(null); // Store customized Excel template data
  // Define customFormHeaders at component scope for use in rendering
  const customFormHeaders = customFormExcel ? customFormExcel[0] : [];

  // apollo client hooks
  const [createManagementProduct, { loading: createManagementProductLoading }] = useMutation(CREATE_MANAGEMENT_PRODUCTS);
  const [getSurvivalData, { loading: loadingSurvival, data: survivalDatasLoad }] = useLazyQuery(GET_SUVIVALS, {
    fetchPolicy: "cache-and-network"
  });

  useEffect(() => {
    // Get shop data from localStorage
    const userData = localStorage.getItem("SHOP_DATA");
    if (userData) {
      const locals = JSON.parse(userData);
      if (locals) {
        fetchSurvivalDatas(locals.id);
      }
    }
  }, [])

  useEffect(() => {
    if (survivalDatasLoad) {
      setSurvivalDatas(survivalDatasLoad?.survivals?.data || []);
    }
  }, [survivalDatasLoad]);


  // Fetch survival data based on shop ID
  const fetchSurvivalDatas = async (shopId) => {
    if (loadingSurvival) return; // Prevent re-triggering if already loading

    try {
      await getSurvivalData({
        variables: {
          where: {
            shop: shopId,
            status: true,
            isDeleted: false
          }
        }
      });
    } catch (err) {
      console.log("Load survival error: ", err);
    }
  }

  const generateNumberByDateTime = () => {
    const date = new Date();
    const year = date.getFullYear().toString();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const seconds = date.getSeconds().toString().padStart(2, "0");

    const generatedNumber = year + month + day + hours + minutes + seconds;
    return generatedNumber.slice(-6);
  };
  
  //* Handle file upload validation data empty
  const handleFileUpload = (event) => {
    setError(null);
    setIsLoading(true);
    
    try {
      const file = event.target.files[0];
      setSelectedFile(file); // Store the selected file
      if (!file) throw new Error("No file selected");
  
      const reader = new FileReader();
  
      reader.onload = async (e) => {
        try {
          const stockDatas = e.target.result;
          const workbook = XLSX.read(stockDatas, { type: "binary" });
          
          if (workbook.SheetNames.length === 0) {
            throw new Error("Excel file contains no sheets");
          }
  
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const rawData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
  
          // Validate at least 2 rows (header + data)
          if (rawData.length < 2) {
            throw new Error("File must contain at least one data row");
          }
          
          // Get header row from the imported Excel
          const headerRow = rawData[0];
          
          // Process data rows (skip header)
          const _newStocks = rawData.slice(1)
            // Filter out empty rows
            .filter(row => row.some(cell => 
              cell !== undefined && 
              cell !== null && 
              String(cell).trim() !== ''
            ))
            // Map valid rows
            .map((row, index) => {
              // Create a base stock object with a unique identifier that's not a hash
              const stock = { 
                id: `item_${index + 1}`, // Use a simple, readable identifier format
                rowNumber: index + 1 // Keep track of row number for display
              };
              
              // Map fields based on the header row
              headerRow.forEach((header, colIndex) => {
                const value = row[colIndex];
                
                // Map each field based on header name
                switch(header) {
                  case 'Barcode':
                    stock.barCode = value?.toString() || generateNumberByDateTime(index);
                    break;
                  case 'sku':
                    stock.sku = value?.toString()?.trim() || `SKU-${index + 1}`;
                    break;
                  case 'ຊື່ສິນຄ້າ':
                    stock.name = value?.toString()?.trim() || `Unnamed Item ${index + 1}`;
                    break;
                  case 'ຫົວຫນວ່ຍ':
                    stock.unit = value?.toString()?.trim() || "";
                    break;
                  case 'ຂະໜາດ':
                    stock.size = value?.toString()?.trim() || "";
                    break;
                  case 'ຈໍານວນ':
                    stock.amount = Math.max(0, Number(value)) || 0;
                    break;
                  case 'ລາຄາຕົ້ນທືນ':
                    stock.originPrice = Math.max(0, Number(value)) || 0;
                    break;
                  case 'ລາຄາ':
                    stock.price = Math.max(0, Number(value)) || 0;
                    break;
                  case 'ລາຄາ1':
                    stock.price1 = Math.max(0, Number(value)) || 0;
                    break;
                  case 'ລາຄາ2':
                    stock.price2 = Math.max(0, Number(value)) || 0;
                    break;
                  case 'ລາຄາ3':
                    stock.price3 = Math.max(0, Number(value)) || 0;
                    break;
                  case 'ສະກຸນເງິນ':
                    stock.currency = value || "ກີບ";
                    break;
                  case 'ປະເພດສີນຄ້າ':
                    stock.categoryName = value?.toString()?.trim() || "";
                    break;
                  case 'ແບຣນ':
                    stock.brandName = value?.toString()?.trim() || "";
                    break;
                  case 'ແບຣນຍ່ອຍ':
                    stock.subBrandName = value?.toString()?.trim() || "";
                    break;
                  case 'ຄ່າຈັດສົ່ງ':
                    stock.shippingCost = Math.max(0, Number(value)) || 0;
                    break;
                  case 'ວັນຜະລີດ':
                    stock.mfg = value?.toString()?.trim() || "";
                    break;
                  case 'ວັນຫມົດອາຍຸ':
                    stock.exp = value?.toString()?.trim() || "";
                    break;
                  case 'ຄວາມສູງ':
                    stock.height = Math.max(0, Number(value)) || 0;
                    break;
                  case 'ຄວາມກ້ວາງ':
                    stock.width = Math.max(0, Number(value)) || 0;
                    break;
                  case 'ຄວາມຍາວ':
                    stock.length = Math.max(0, Number(value)) || 0;
                    break;
                  case 'ນ້ຳໜັກ':
                    stock.weight = Math.max(0, Number(value)) || 0;
                    break;
                  // Add more cases for other fields as needed
                }
              });
              
              // Ensure required fields have default values if not set
              if (!stock.barCode) stock.barCode = generateNumberByDateTime(index);
              if (!stock.name) stock.name = `Unnamed Item ${index + 1}`;
              if (!stock.currency) stock.currency = "ກີບ";
              
              return stock;
            });
  
          if (_newStocks.length === 0) {
            throw new Error("No valid data found in the spreadsheet");
          }
  
          setStockDatas(_newStocks);
          setImportStep(3); // Move to review step
          setIsLoading(false);
  
        } catch (error) {
          console.error("Processing error:", error);
          setError(error.message);
          setIsLoading(false);
        }
      };
  
      reader.onerror = () => {
        setError("Failed to read file");
        setIsLoading(false);
      };
  
      reader.readAsBinaryString(file);
  
    } catch (error) {
      console.error("Upload error:", error);
      setError(error.message);
      setIsLoading(false);
    }
  };

  const handleCreateManagementProduct = async () => {
    try {
      if (createManagementProductLoading) return;
      let stocks = [];
      for (let stock of stockDatas) {
        if(!stock?.barCode) return errorSwal(`ກະລຸນາປ້ອນBarcode`);
        if(!stock?.sku) return errorSwal(`ກະລຸນາປ້ອນsku`);
        if(!stock?.originPrice || stock?.originPrice <= 0) return errorSwal(`ກະລຸນາປ້ອນລາຄາຕົ້ນທືນ`);
        if(!stock?.price || stock?.price <= 0) return errorSwal(`ກະລຸນາປ້ອນລາຄາ`);
        if(!stock?.amount || stock?.amount <= 0) return errorSwal(`ກະລຸນາປ້ອນຈໍານວນ`);
        if(!stock?.name) return errorSwal(`ກະລຸນາປ້ອນຊື່ສິນຄ້າ`);
        if(!stock?.categoryName) return errorSwal(`ກະລຸນາປ້ອນປະເພດສີນຄ້າ`);
        if(!stock?.brandName) return errorSwal(`ກະລຸນາປ້ອນແບຣນ`);
        if(!stock?.unit) return errorSwal(`ກະລຸນາປ້ອນຫົວຫນວ່ຍ`);
        if(!stock?.size) return errorSwal(`ກະລຸນາປ້ອນຂະໜາດ`);
        if(!stock?.mfg) return errorSwal(`ກະລຸນາປ້ອນວັນຜະລີດ`);
        if(!stock?.exp) return errorSwal(`ກະລຸນາປ້ອນວັນຫມົດອາຍຸ`);
        let priceOptions = [];
        if (stock.price1 > 0)
          priceOptions.push({ price: parseFloat(stock.price1 ? stock.price1.toString().replace(/,/g, "") : 0), contain: "1" });
        if (stock.price2 > 0)
          priceOptions.push({ price: parseFloat(stock.price2 ? stock.price2.toString().replace(/,/g, "") : 0), contain: "2" });
        if (stock.price3 > 0)
          priceOptions.push({ price: parseFloat(stock.price3 ? stock.price3.toString().replace(/,/g, "") : 0), contain: "3" });

        const _newStock = {
          barCode: stock?.barCode,
          sku: stock?.sku,
          name: stock?.name,
          unitName: stock?.unit,
          size: stock?.size,
          categoryName: stock?.categoryName,
          brandName: stock?.brandName,
          subBrandName: stock?.subBrandName,
          amount: parseFloat(stock?.amount ? stock?.amount.toString().replace(/,/g, "") : 0),
          price: parseFloat(stock?.price ? stock?.price.toString().replace(/,/g, "") : 0),
          originPrice:
            parseFloat(stock?.originPrice ? stock?.originPrice.toString().replace(/,/g, "") : 0),
          priceOptions,
          currency: HandleCurrency(stock?.currency),
          note: stock?.note ?? "",
          shippingCost: parseFloat(stock?.shippingCost ? stock?.shippingCost.toString().replace(/,/g, "") : 0),
          mfg: stock?.mfg ?? "",
          exp: stock?.exp ?? "",
          dimension: {
            length: parseFloat(stock?.length ? stock?.length.toString().replace(/,/g, "") : 0),
            width: parseFloat(stock?.width ? stock?.width.toString().replace(/,/g, "") : 0),
            height: parseFloat(stock?.height ? stock?.height.toString().replace(/,/g, "") : 0),
            weight: parseFloat(stock?.weight ? stock?.weight.toString().replace(/,/g, "") : 0)
          },
          platform: "POS",
          type: "EXCEL"
        };
        stocks.push({ ..._newStock });
      }

      // Lot
      if (_.isEmpty(selectSurvival)) {
        return errorSwal(`ກະລຸນາເລືອກລ໊ອດ`);
      }
      // create products
      const { data } = await createManagementProduct({
        variables: {
          data: stocks,
          where: {
            lot: selectSurvival,
          },
        },
      });
      console.log("data?.createManagementProduct", data?.createManagementProduct);
      const {status, msg} = data?.createManagementProduct;
      if (status) {
        successSwal(msg);
        history.goBack();
      } else {
        errorSwal(msg);
      }

    } catch (error) {
      console.log(error);
      errorSwal(`ເກີດຂໍ້ຜິດພາດ`);
    }
  };

  // Default Excel template data
  const defaultFormExcel = [
    [
        "Barcode", "sku", "ຊື່ສິນຄ້າ", "ປະເພດສີນຄ້າ", "ຫົວຫນວ່ຍ", "ຂະໜາດ", "ແບຣນ", "ແບຣນຍ່ອຍ", "ຈໍານວນ", "ລາຄາຕົ້ນທືນ", "ລາຄາ", "ລາຄາ1", "ລາຄາ2", "ລາຄາ3", "ຄ່າຈັດສົ່ງ", "ສະກຸນເງິນ", "ວັນຜະລີດ", "ວັນຫມົດອາຍຸ", "ຄວາມສູງ", "ຄວາມກ້ວາງ", "ຄວາມຍາວ", "ນ້ຳໜັກ"
    ],
    [
        "00001", "00001M", "ກະເປົາເງີນ Balenciaga", "ກະເປົາ", "ອັນ", "M", "Balenciaga", "Balenciaga 2", 10, 50000, 900000, 100000, 150000, 250000, 9000, "LAK", '2024-04-01', '2024-04-30', 0.5, 0.5, 0.5, 0.5
    ],
    [
        "00002", "00002M", "ກະເປົາເງີນ Gucci", "ກະເປົາ", "ອັນ", "M", "Gucci", "Gucci 2", 10, 100, 200, 199, 250, 300, 9, "BAHT", '2024-04-01', '2024-04-30', 0.5, 0.5, 0.5, 0.5
    ],
    [
        "00003", "00003M", "ກະເປົາເງີນ Dior", "ກະເປົາ", "ອັນ", "M", "Dior", "Dior 2", 10, 8, 10, 11, 15, 20, 0.5, "USD", '2024-04-01', '2024-04-30', 0.5, 0.5, 0.5, 0.5
    ],
  ];
  
  // Use customFormExcel if available, otherwise use default
  const formExcel = customFormExcel || defaultFormExcel;
  
  // State to track if Excel file has been downloaded
  const [excelDownloaded, setExcelDownloaded] = useState(true);
  const [fieldsMatched, setFieldsMatched] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // Handle custom form data from ExcelFormProductManage
  const handleCustomFormData = (formData, isDownloaded) => {
    setCustomFormExcel(formData);
    setExcelDownloaded(isDownloaded);
    // Don't set fieldsMatched here - it will be set after validation
    
    // If the file was downloaded, automatically go to step 2
    if (isDownloaded) {
      setImportStep(2);
    }
  };

  // Validate Excel file against selected fields
  // Add this function after the handleCustomFormData function
const validateExcelFile = (file) => {
  if (!file) {
    setFieldsMatched(false);
    return;
  }
  
  // Use customFormHeaders defined at component level
  
  if (customFormHeaders.length === 0) {
    setError("No fields were selected in step 1. Please go back and select fields.");
    setFieldsMatched(false);
    return;
  }
  
  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const data = e.target.result;
      const workbook = XLSX.read(data, { type: "binary" });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const rawData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      
      if (rawData.length < 2) {
        setError("File must contain at least one data row");
        setFieldsMatched(false);
        return;
      }
      
      // Get header row from the imported Excel
      const headerRow = rawData[0];
      
      // Check if all required fields are present
      const missingFields = [];
      for (const field of customFormHeaders) {
        if (!headerRow.includes(field)) {
          missingFields.push(field);
        }
      }
      
      if (missingFields.length > 0) {
        setError(`Missing required fields in Excel file: ${missingFields.join(', ')}`);
        setFieldsMatched(false);
        return;
      }
      
      // All fields match, set the state to true
      setFieldsMatched(true);
      setError(null);
    } catch (error) {
      console.error("Excel validation error:", error);
      setError(error.message);
      setFieldsMatched(false);
    }
  };
  
  reader.onerror = () => {
    setError("Failed to read file");
    setFieldsMatched(false);
  };
  
  reader.readAsBinaryString(file);
};
  
  // Handle proceeding to next step
  const handleNextStep = () => {
    console.log("fieldsMatched:", fieldsMatched);
    if (excelDownloaded && fieldsMatched) {
      setImportStep(2);
    }
  };
  
  // Reset the import process
  const resetImport = () => {
    setImportStep(1);
    setStockDatas([]);
    setCustomFormExcel(null);
    setError(null);
    setExcelDownloaded(false);
    setFieldsMatched(false);
    setSelectedFile(null);
  };

  return (
    <div style={{ padding: "2em" }}>
      <BreadCrumbComponent goback={() => history.goBack()}
        txtFirst="ສາງສິນຄ້າ"
        txtSecond="ການຈັດການທີ່ຄວາບຄຸມ ລ໊ອດ, ຫມົດອາຍຸ, sku,... ເພິ່ມສິນຄ້າຈາກ Excel" />

      {/* Step 1: Select fields for Excel template */}
      {importStep === 1 && (
        <Card className="mb-4 p-3">
          <Card.Title>ຂັ້ນຕອນທີ 1: ເລືອກຟິວທີ່ຕ້ອງການນໍາເຂົ້າ</Card.Title>
          <Card.Body>
            <p>ເລືອກຟິວທີ່ທ່ານຕ້ອງການລວມໃນແບບຟອມ Excel ຂອງທ່ານ. ຫຼັງຈາກນັ້ນທ່ານສາມາດດາວໂຫຼດແບບຟອມ Excel ແລະຕື່ມຂໍ້ມູນຂອງທ່ານ.</p>
            <ExcelFormProductManage 
              dataForm={formExcel} 
              onCustomFormGenerated={handleCustomFormData}
            />
            <div className="d-flex mt-3">
              <Button 
                onClick={handleNextStep}
                style={{
                  background: LINEAR_GRADIENT_BG,
                  border: "none",
                  color: COLOR_WHITE
                }}
                disabled={!excelDownloaded || !fieldsMatched}
              >
                ຕໍ່ໄປ
              </Button>
              {!excelDownloaded && (
                <div className="text-danger ms-3 d-flex align-items-center">
                  ກະລຸນາດາວໂຫຼດແບບຟອມ Excel ແລະຈັດຄູ່ຟິວກ່ອນ
                </div>
              )}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Step 2: Import data from Excel */}
      {importStep === 2 && (
        <Card className="mb-4 p-3">
          <Card.Title>ຂັ້ນຕອນທີ 2: ນໍາເຂົ້າຂໍ້ມູນຈາກ Excel</Card.Title>
          <Card.Body>
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>ເລືອກ File Excel ຂອງທ່ານ</Form.Label>
                  <Form.Control 
                    type="file" 
                    key={importStep} // Re-create input when step changes
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setSelectedFile(file);
                        setError(null);
                        setFieldsMatched(false); // Reset validation on new file
                        validateExcelFile(file);
                      }
                    }} 
                  />
                  {error && <div className="text-danger mt-2">{error}</div>}
                  {fieldsMatched && <div className="text-success mt-2">ຟິວທີ່ເລືອກແລະຟິວທີ່ນຳເຂົ້າຕຣງກັນ</div>}
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>ໝາຍເລກລອດ</Form.Label>
                  <Form.Select 
                    onChange={(e) => setSelectSurvival(e?.target?.value)} 
                    value={selectSurvival} 
                    aria-label="Default select example"
                  >
                    <option>--ເລືອກ--</option>
                    {survivalDatas.map((_option, _index) => (
                      <option key={_index} value={_option?.id}>{_option?.survivalNumber}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <div className="d-flex">
            <Button 
              style={{
                background: LINEAR_GRADIENT_BG,
                border: "none",
                color: COLOR_WHITE,
                marginRight: "10px"
              }}
              onClick={() => {
                // If validation has passed and we have a file selected
                if (selectedFile && fieldsMatched) {
                  // Create a new synthetic event object with the file
                  const syntheticEvent = {
                    target: {
                      files: [selectedFile]
                    }
                  };
                  
                  // Call the original handleFileUpload function to process the file
                  handleFileUpload(syntheticEvent);
                } else if (!fieldsMatched) {
                  setError("ກະລຸນາກວດສອບວ່າຟິວທີ່ເລືອກແລະຟິວທີ່ນຳເຂົ້າຕຣງກັນ");
                } else {
                  setError("ກະລຸນາເລືອກໄຟລ໌ Excel");
                }
              }}
              disabled={!excelDownloaded || !selectedFile || !fieldsMatched}
            >
              ຕໍ່ໄປ
            </Button>
              <Button 
                variant="outline-secondary" 
                onClick={resetImport} 
                className="me-2"
              >
                ກັບຄືນ
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Step 3: Review imported data */}
      {importStep === 3 && (
        <Card className="mb-4 p-3">
          <Card.Title>ຂັ້ນຕອນທີ 3: ກວດສອບຂໍ້ມູນທີ່ນໍາເຂົ້າ</Card.Title>
          <Card.Body>
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>ໝາຍເລກລອດ</Form.Label>
                  <Form.Select 
                    onChange={(e) => setSelectSurvival(e?.target?.value)} 
                    value={selectSurvival} 
                    aria-label="Default select example"
                  >
                    <option>--ເລືອກ--</option>
                    {survivalDatas.map((_option, _index) => (
                      <option key={_index} value={_option?.id}>{_option?.survivalNumber}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={8} className="d-flex align-items-end">
                <Button
                  disabled={stockDatas?.length <= 0 || createManagementProductLoading}
                  style={{
                    background: LINEAR_GRADIENT_BG,
                    border: "none",
                    color: COLOR_WHITE,
                    marginRight: "10px"
                  }}
                  onClick={() => {
                    if (stockDatas?.length <= 0) return;
                    handleCreateManagementProduct();
                  }}
                >
                  {createManagementProductLoading ? (
                    <Spinner size="sm" animation="border" />
                  ) : (
                    "ບັນທືກ"
                  )}
                </Button>
                <Button
                  variant="outline-danger"
                  onClick={resetImport}
                  disabled={createManagementProductLoading}
                >
                  ຍົກເລີກ
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {isLoading && <AnimationLoading />}

      <Card style={{ border: "none", marginTop: "1em" }}>
        {/* Excel-like table styles */}
        <style>
          {
            `
            .excel-table {
              border-collapse: collapse;
              width: 100%;
              font-size: 0.85rem;
            }
            .excel-table th {
              background-color: #f2f2f2;
              border: 1px solid #ddd;
              padding: 4px 8px;
              font-weight: 700;
              text-align: center;
              color: #000;
            }
            .excel-table td {
              border: 1px solid #ddd;
              padding: 0;
              height: 32px;
            }
            .excel-table .form-control {
              border: none;
              border-radius: 0;
              height: 32px;
              padding: 4px 8px;
              font-size: 0.85rem;
            }
            .excel-table .form-control:focus {
              box-shadow: none;
              background-color: #f0f8ff;
            }
            .excel-table .btn-group .btn {
              padding: 2px 4px;
              font-size: 0.8rem;
              height: 32px;
            }
            .excel-table .row-number {
              background-color: #f2f2f2;
              text-align: center;
              font-weight: 500;
              padding: 4px 8px;
            }
            .excel-table .btn-delete {
              padding: 0;
              width: 100%;
              height: 32px;
              display: flex;
              align-items: center;
              justify-content: center;
              border: none;
              background: transparent;
            }
            .excel-table .btn-delete:hover {
              background-color: #ffeeee;
            }
            `
          }
        </style>
        {importStep === 3 && stockDatas?.length > 0 && !isLoading && (
          <Table responsive className="excel-table">
            <thead>
              <tr style={{ whiteSpace: "nowrap" }}>
                <th>#</th>
                {/* Dynamically render table headers based on selected fields */}
                {customFormHeaders.includes('Barcode') && (
                  <th>ບຣາໂຄດ</th>
                )}
                {customFormHeaders.includes('sku') && (
                  <th style={{ minWidth: '80px' }}>sku</th>
                )}
                {customFormHeaders.includes('ຊື່ສິນຄ້າ') && (
                  <th style={{ minWidth: '125px' }}>ຊື່ສິນຄ້າ</th>
                )}
                {customFormHeaders.includes('ຫົວຫນວ່ຍ') && (
                  <th>ຫົວຫນວ່ຍ</th>
                )}
                {customFormHeaders.includes('ຂະໜາດ') && (
                  <th>ຂະໜາດ</th>
                )}
                {customFormHeaders.includes('ຈໍານວນ') && (
                  <th>ຈໍານວນ</th>
                )}
                {customFormHeaders.includes('ລາຄາຕົ້ນທືນ') && (
                  <th>ລາຄາຕົ້ນທືນ</th>
                )}
                {customFormHeaders.includes('ລາຄາ1') && (
                  <th>ລາຄາຂາຍ 1</th>
                )}
                {customFormHeaders.includes('ລາຄາ') && (
                  <th style={{ minWidth: '80px' }}>ລາຄາ</th>
                )}
                {customFormHeaders.includes('ລາຄາ2') && (
                  <th>ລາຄາຂາຍ 2</th>
                )}
                {customFormHeaders.includes('ລາຄາ3') && (
                  <th>ລາຄາຂາຍ 3</th>
                )}
                {customFormHeaders.includes('ສະກຸນເງິນ') && (
                  <th>ສະກຸນເງິນ</th>
                )}
                {customFormHeaders.includes('ປະເພດສີນຄ້າ') && (
                  <th>ປະເພດ</th>
                )}
                {customFormHeaders.includes('ແບຣນ') && (
                  <th>ແບຣນ</th>
                )}
                {customFormHeaders.includes('ແບຣນຍ່ອຍ') && (
                  <th>ຊັບແບຣນ</th>
                )}
                {customFormHeaders.includes('ຄ່າຈັດສົ່ງ') && (
                  <th>ຄ່າຈັດສົ່ງ</th>
                )}
                {customFormHeaders.includes('ວັນຜະລີດ') && (
                  <th>ວັນຜະລີດ</th>
                )}
                {customFormHeaders.includes('ວັນຫມົດອາຍຸ') && (
                  <th>ວັນຫມົດອາຍຸ</th>
                )}
                {customFormHeaders.includes('ຄວາມສູງ') && (
                  <th>ຄວາມສູງ</th>
                )}
                {customFormHeaders.includes('ຄວາມກ້ວາງ') && (
                  <th>ຄວາມກ້ວາງ</th>
                )}
                {customFormHeaders.includes('ຄວາມຍາວ') && (
                  <th>ຄວາມຍາວ</th>
                )}
                {customFormHeaders.includes('ນ້ຳໜັກ') && (
                  <th>ນ້ຳໜັກ</th>
                )}
                <th>ຈັດການ</th>
              </tr>
            </thead>
            <tbody>
              {stockDatas?.length > 0 &&
                stockDatas?.map((stock, index) => (
                  <tr key={index} style={{ whiteSpace: "nowrap" }}>
                    <td className="row-number">{stock.rowNumber || index + 1}</td>
                    
                    {/* Dynamically render table cells based on selected fields */}
                    {customFormHeaders.includes('Barcode') && (
                      <td>
                        <Form.Control
                          type="input"
                          value={stock?.barCode}
                          onChange={(e) => {
                            const _index = _.findIndex(stockDatas, {
                              id: stock?.id,
                            });
                            let _newStocks = [...stockDatas];
                            _newStocks[_index].barCode = e.target.value;
                            setStockDatas(_newStocks);
                          }}
                        />
                      </td>
                    )}
                    
                    {customFormHeaders.includes('sku') && (
                      <td style={{ minWidth: '80px' }}>
                        <Form.Control
                          type="input"
                          value={stock?.sku}
                          onChange={(e) => {
                            const _index = _.findIndex(stockDatas, {
                              id: stock?.id,
                            });
                            let _newStocks = [...stockDatas];
                            _newStocks[_index].sku = e.target.value;
                            setStockDatas(_newStocks);
                          }}
                        />
                      </td>
                    )}
                    
                    {customFormHeaders.includes('ຊື່ສິນຄ້າ') && (
                      <td style={{ minWidth: '125px' }}>
                        <Form.Control
                          type="input"
                          value={stock?.name}
                          onChange={(e) => {
                            const _index = _.findIndex(stockDatas, {
                              id: stock?.id,
                            });
                            let _newStocks = [...stockDatas];
                            _newStocks[_index].name = e.target.value;
                            setStockDatas(_newStocks);
                          }}
                        />
                      </td>
                    )}
                    
                    {customFormHeaders.includes('ຫົວຫນວ່ຍ') && (
                      <td>
                        <Form.Control
                          type="input"
                          name="unit"
                          value={stock?.unit}
                          onChange={(e) => {
                            const _index = _.findIndex(stockDatas, {
                              id: stock?.id,
                            });
                            let _newStocks = [...stockDatas];
                            _newStocks[_index].unit = e.target.value;
                            setStockDatas(_newStocks);
                          }}
                        />
                      </td>
                    )}
                    
                    {customFormHeaders.includes('ຂະໜາດ') && (
                      <td>
                        <Form.Control
                          type="input"
                          name="size"
                          value={stock?.size}
                          onChange={(e) => {
                            const _index = _.findIndex(stockDatas, {
                              id: stock?.id,
                            });
                            let _newStocks = [...stockDatas];
                            _newStocks[_index].size = e.target.value;
                            setStockDatas(_newStocks);
                          }}
                        />
                      </td>
                    )}
                    
                    {customFormHeaders.includes('ຈໍານວນ') && (
                      <td>
                        <NumberFormatBase
                          name="amount"
                          onChange={(e) => {
                            const _index = _.findIndex(stockDatas, {
                              id: stock?.id,
                            });
                            let _newStocks = [...stockDatas];
                            _newStocks[_index].amount = e.target.value;
                            setStockDatas(_newStocks);
                          }}
                          value={stock?.amount}
                          className="form-control"
                          placeholder="0"
                          thousandSeparator={true}
                          prefix={""}
                        />
                      </td>
                    )}
                    
                    {customFormHeaders.includes('ລາຄາຕົ້ນທືນ') && (
                      <td>
                        <NumberFormatBase
                          name="originPrice"
                          format={(val) => {
                            return val.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                          }}
                          onChange={(e) => {
                            const _index = _.findIndex(stockDatas, {
                              id: stock?.id,
                            });
                            let _newStocks = [...stockDatas];
                            _newStocks[_index].originPrice = e.target.value.replace(/\,/g, '');
                            setStockDatas(_newStocks);
                          }}
                          value={stock?.originPrice}
                          className="form-control"
                          placeholder="0"
                        />
                      </td>
                    )}

                    {customFormHeaders.includes('ລາຄາ') && (
                      <td>
                        <NumberFormatBase
                          name="price"
                          format={(val) => {
                            return val.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                          }}
                          onChange={(e) => {
                            const _index = _.findIndex(stockDatas, {
                              id: stock?.id,
                            });
                            let _newStocks = [...stockDatas];
                            _newStocks[_index].price = e.target.value.replace(/\,/g, '');
                            setStockDatas(_newStocks);
                          }}
                          value={stock?.price}
                          className="form-control"
                          placeholder="0"
                        />
                      </td>
                    )}
                    
                    {customFormHeaders.includes('ລາຄາ1') && (
                      <td>
                        <NumberFormatBase
                          name="price1"
                          format={(val) => {
                            return val.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                          }}
                          onChange={(e) => {
                            const _index = _.findIndex(stockDatas, {
                              id: stock?.id,
                            });
                            let _newStocks = [...stockDatas];
                            _newStocks[_index].price1 = e.target.value.replace(/\,/g, '');
                            setStockDatas(_newStocks);
                          }}
                          value={stock?.price1}
                          className="form-control"
                          placeholder="0"
                        />
                      </td>
                    )}
                    
                    {customFormHeaders.includes('ລາຄາ2') && (
                      <td>
                        <NumberFormatBase
                          name="price2"
                          format={(val) => {
                            return val.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                          }}
                          onChange={(e) => {
                            const _index = _.findIndex(stockDatas, {
                              id: stock?.id,
                            });
                            let _newStocks = [...stockDatas];
                            _newStocks[_index].price2 = e.target.value.replace(/\,/g, '');
                            setStockDatas(_newStocks);
                          }}
                          value={stock?.price2}
                          className="form-control"
                          placeholder="0"
                        />
                      </td>
                    )}
                    
                    {customFormHeaders.includes('ລາຄາ3') && (
                      <td>
                        <NumberFormatBase
                          name="price3"
                          format={(val) => {
                            return val.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                          }}
                          onChange={(e) => {
                            const _index = _.findIndex(stockDatas, {
                              id: stock?.id,
                            });
                            let _newStocks = [...stockDatas];
                            _newStocks[_index].price3 = e.target.value.replace(/\,/g, '');
                            setStockDatas(_newStocks);
                          }}
                          value={stock?.price3}
                          className="form-control"
                          placeholder="0"
                        />
                      </td>
                    )}
                    
                    {customFormHeaders.includes('ສະກຸນເງິນ') && (
                      <td>
                        <div style={{ display: 'flex', height: '100%' }}>
                          <Button
                            size="sm"
                            style={{
                              flex: 1,
                              backgroundColor: HandleCurrency(stock?.currency) === "LAK" ? "#28a745" : "#f8f9fa",
                              color: HandleCurrency(stock?.currency) === "LAK" ? "#fff" : "#212529",
                              borderRadius: 0,
                              border: 'none',
                              fontSize: '0.75rem',
                              padding: '0 4px'
                            }}
                            onClick={() => {
                              const _index = _.findIndex(stockDatas, { id: stock?.id });
                              let _newStocks = [...stockDatas];
                              _newStocks[_index].currency = "ກີບ";
                              setStockDatas(_newStocks);
                            }}>
                            ກີບ
                          </Button>
                          <Button
                            size="sm"
                            style={{
                              flex: 1,
                              backgroundColor: HandleCurrency(stock?.currency) === "BAHT" ? "#28a745" : "#f8f9fa",
                              color: HandleCurrency(stock?.currency) === "BAHT" ? "#fff" : "#212529",
                              borderRadius: 0,
                              border: 'none',
                              fontSize: '0.75rem',
                              padding: '0 4px'
                            }}
                            onClick={() => {
                              const _index = _.findIndex(stockDatas, { id: stock?.id });
                              let _newStocks = [...stockDatas];
                              _newStocks[_index].currency = "ບາດ";
                              setStockDatas(_newStocks);
                            }}>
                            ບາດ
                          </Button>
                          <Button
                            size="sm"
                            style={{
                              flex: 1,
                              backgroundColor: HandleCurrency(stock?.currency) === "USD" ? "#28a745" : "#f8f9fa",
                              color: HandleCurrency(stock?.currency) === "USD" ? "#fff" : "#212529",
                              borderRadius: 0,
                              border: 'none',
                              fontSize: '0.75rem',
                              padding: '0 4px'
                            }}
                            onClick={() => {
                              const _index = _.findIndex(stockDatas, { id: stock?.id });
                              let _newStocks = [...stockDatas];
                              _newStocks[_index].currency = "ໂດລາ";
                              setStockDatas(_newStocks);
                            }}>
                            ໂດລາ
                          </Button>
                        </div>
                      </td>
                    )}
                    
                    {customFormHeaders.includes('ປະເພດສີນຄ້າ') && (
                      <td>
                        <Form.Control
                          type="input"
                          value={stock?.categoryName}
                          onChange={(e) => {
                            const _index = _.findIndex(stockDatas, {
                              id: stock?.id,
                            });
                            let _newStocks = [...stockDatas];
                            _newStocks[_index].categoryName = e.target.value;
                            setStockDatas(_newStocks);
                          }}
                        />
                      </td>
                    )}
                    
                    {customFormHeaders.includes('ແບຣນ') && (
                      <td>
                        <Form.Control
                          type="input"
                          value={stock?.brandName}
                          onChange={(e) => {
                            const _index = _.findIndex(stockDatas, {
                              id: stock?.id,
                            });
                            let _newStocks = [...stockDatas];
                            _newStocks[_index].brandName = e.target.value;
                            setStockDatas(_newStocks);
                          }}
                        />
                      </td>
                    )}
                    
                    {customFormHeaders.includes('ແບຣນຍ່ອຍ') && (
                      <td>
                        <Form.Control
                          type="input"
                          value={stock?.subBrandName}
                          onChange={(e) => {
                            const _index = _.findIndex(stockDatas, {
                              id: stock?.id,
                            });
                            let _newStocks = [...stockDatas];
                            _newStocks[_index].subBrandName = e.target.value;
                            setStockDatas(_newStocks);
                          }}
                        />
                      </td>
                    )}
                    
                    {customFormHeaders.includes('ຄ່າຈັດສົ່ງ') && (
                      <td>
                        <NumberFormatBase
                          name="shippingCost"
                          format={(val) => {
                            return val.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                          }}
                          onChange={(e) => {
                            const _index = _.findIndex(stockDatas, {
                              id: stock?.id,
                            });
                            let _newStocks = [...stockDatas];
                            _newStocks[_index].shippingCost = e.target.value.replace(/\,/g, '');
                            setStockDatas(_newStocks);
                          }}
                          value={stock?.shippingCost}
                          className="form-control"
                          placeholder="0"
                        />
                      </td>
                    )}
                    
                    {customFormHeaders.includes('ວັນຜະລີດ') && (
                      <td>
                        <Form.Control
                          type="date"
                          value={stock?.mfg}
                          onChange={(e) => {
                            const _index = _.findIndex(stockDatas, {
                              id: stock?.id,
                            });
                            let _newStocks = [...stockDatas];
                            _newStocks[_index].mfg = e.target.value;
                            setStockDatas(_newStocks);
                          }}
                        />
                      </td>
                    )}
                    
                    {customFormHeaders.includes('ວັນຫມົດອາຍຸ') && (
                      <td>
                        <Form.Control
                          type="date"
                          value={stock?.exp}
                          onChange={(e) => {
                            const _index = _.findIndex(stockDatas, {
                              id: stock?.id,
                            });
                            let _newStocks = [...stockDatas];
                            _newStocks[_index].exp = e.target.value;
                            setStockDatas(_newStocks);
                          }}
                        />
                      </td>
                    )}

                    {customFormHeaders.includes('ຄວາມສູງ') && (
                      <td>
                        <Form.Control
                          type="text"
                          inputMode="decimal"
                          pattern="[0-9]*(\.[0-9]+)?"
                          value={stock?.height || ''}
                          onChange={(e) => {
                            // Only allow numbers and a single decimal point
                            const value = e.target.value.replace(/,/g, '.').replace(/[^0-9.]/g, '');
                            // Prevent multiple decimal points
                            const parts = value.split('.');
                            const formattedValue = parts[0] + (parts.length > 1 ? '.' + parts.slice(1).join('') : '');
                            
                            const _index = _.findIndex(stockDatas, {
                              id: stock?.id,
                            });
                            let _newStocks = [...stockDatas];
                            _newStocks[_index].height = formattedValue;
                            setStockDatas(_newStocks);
                          }}
                          className="form-control"
                          placeholder="0"
                        />
                      </td>
                    )}
                    
                    {customFormHeaders.includes('ຄວາມກ້ວາງ') && (
                      <td>
                        <Form.Control
                          type="text"
                          inputMode="decimal"
                          pattern="[0-9]*(\.[0-9]+)?"
                          value={stock?.width || ''}
                          onChange={(e) => {
                            // Only allow numbers and a single decimal point
                            const value = e.target.value.replace(/,/g, '.').replace(/[^0-9.]/g, '');
                            // Prevent multiple decimal points
                            const parts = value.split('.');
                            const formattedValue = parts[0] + (parts.length > 1 ? '.' + parts.slice(1).join('') : '');
                            
                            const _index = _.findIndex(stockDatas, {
                              id: stock?.id,
                            });
                            let _newStocks = [...stockDatas];
                            _newStocks[_index].width = formattedValue;
                            setStockDatas(_newStocks);
                          }}
                          className="form-control"
                          placeholder="0"
                        />
                      </td>
                    )}
                    
                    {customFormHeaders.includes('ຄວາມຍາວ') && (
                      <td>
                        <Form.Control
                          type="text"
                          inputMode="decimal"
                          pattern="[0-9]*(\.[0-9]+)?"
                          value={stock?.length || ''}
                          onChange={(e) => {
                            // Only allow numbers and a single decimal point
                            const value = e.target.value.replace(/,/g, '.').replace(/[^0-9.]/g, '');
                            // Prevent multiple decimal points
                            const parts = value.split('.');
                            const formattedValue = parts[0] + (parts.length > 1 ? '.' + parts.slice(1).join('') : '');
                            
                            const _index = _.findIndex(stockDatas, {
                              id: stock?.id,
                            });
                            let _newStocks = [...stockDatas];
                            _newStocks[_index].length = formattedValue;
                            setStockDatas(_newStocks);
                          }}
                          className="form-control"
                          placeholder="0"
                        />
                      </td>
                    )}
                    
                    {customFormHeaders.includes('ນ້ຳໜັກ') && (
                      <td>
                        <Form.Control
                          type="text"
                          inputMode="decimal"
                          pattern="[0-9]*(\.[0-9]+)?"
                          value={stock?.weight || ''}
                          onChange={(e) => {
                            // Only allow numbers and a single decimal point
                            const value = e.target.value.replace(/,/g, '.').replace(/[^0-9.]/g, '');
                            // Prevent multiple decimal points
                            const parts = value.split('.');
                            const formattedValue = parts[0] + (parts.length > 1 ? '.' + parts.slice(1).join('') : '');
                            
                            const _index = _.findIndex(stockDatas, {
                              id: stock?.id,
                            });
                            let _newStocks = [...stockDatas];
                            _newStocks[_index].weight = formattedValue;
                            setStockDatas(_newStocks);
                          }}
                          className="form-control"
                          placeholder="0"
                        />
                      </td>
                    )}
                    <td>
                      <button
                        className="btn-delete"
                        onClick={() => {
                          let _newStocks = [...stockDatas];
                          _.remove(_newStocks, { id: stock?.id });
                          setStockDatas(_newStocks)
                          successSwal(`ລົບຂໍ້ມູນສໍາເລັດແລ້ວ`);
                        }}>
                        <FontAwesomeIcon
                          icon={faTrashAlt}
                          style={{ color: "red" }}
                        />
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        )}

      </Card>

    </div>
  );
}

export default AddProductManagement;